import { useState, useEffect } from 'react';
import { FolderInput, X, Loader2, Folder, Check } from 'lucide-react';
import { getProjects, type ProjectData } from '@/api/modules/sessions';
import { cn } from '@/utils/cn';

export interface ProjectSelectDialogProps {
  open: boolean;
  onClose: () => void;
  /** 点击「保存」时回调，返回的 Promise 结束后才关闭弹窗（父组件负责调 attach 接口） */
  onConfirm: (projectId: string) => Promise<void>;
  /** 弹窗副标题展示用（如试玩名称） */
  playableLabel?: string;
}

/**
 * 「保存到项目」选择弹窗（用户维度存储方案 P3 前端入口）
 *
 * 用于把未挂靠项目的试玩（sandbox）挂载到某个项目：
 * 拉取当前用户可见的项目列表（GET /api/projects），单选后由父组件调用
 * POST /api/playables/:id/attach 完成后端归属变更。
 */
export function ProjectSelectDialog({ open, onClose, onConfirm, playableLabel }: ProjectSelectDialogProps) {
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setSubmitting(false);
    setLoading(true);
    getProjects()
      .then((res) => setProjects(res.data ?? []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const handleConfirm = async () => {
    if (!selectedId || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(selectedId);
      onClose();
    } catch {
      // 父组件已 toast 报错，保持弹窗打开便于重试
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="glass-card flex max-h-[80vh] w-full max-w-md flex-col p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-neon-cyan to-neon-purple">
            <FolderInput size={18} className="text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-foreground">保存到项目</h3>
            <p className="truncate text-xs text-muted-foreground">
              {playableLabel ? `将「${playableLabel}」挂靠到项目，便于归档管理` : '选择一个项目挂靠该试玩'}
            </p>
          </div>
          <button onClick={onClose} className="icon-btn ml-auto rounded p-1"><X size={16} /></button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-3 py-12">
            <Loader2 size={24} className="animate-spin text-neon-cyan" />
            <span className="text-xs text-muted-foreground">加载项目列表...</span>
          </div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <Folder size={28} className="text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">暂无可用项目</p>
            <p className="text-xs text-muted-foreground/70">请先在「创作中心」创建一个项目</p>
          </div>
        ) : (
          <div className="mb-4 flex-1 overflow-y-auto pr-1">
            <div className="flex flex-col gap-1.5">
              {projects.map((proj) => {
                const active = proj.id === selectedId;
                return (
                  <button
                    key={proj.id}
                    onClick={() => setSelectedId(proj.id)}
                    className={cn(
                      'flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors',
                      active
                        ? 'border-primary/50 bg-primary/15'
                        : 'border-border/40 bg-muted/20 hover:border-border hover:bg-muted/40',
                    )}
                  >
                    <span className="text-lg">{proj.icon || '📁'}</span>
                    <span className="min-w-0 flex-1">
                      <span className={cn('block truncate text-sm font-medium', active ? 'text-primary' : 'text-foreground')}>
                        {proj.name}
                      </span>
                      {proj.description && (
                        <span className="block truncate text-[11px] text-muted-foreground">{proj.description}</span>
                      )}
                    </span>
                    {active && <Check size={14} className="shrink-0 text-primary" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-border/40 pt-4">
          <button onClick={onClose} className="icon-btn rounded-lg px-4 py-2 text-sm">取消</button>
          <button
            onClick={handleConfirm}
            disabled={!selectedId || submitting || loading}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            保存
          </button>
        </div>
      </div>
    </div>
  );
}
